import React, { useState, useEffect, useRef } from 'react';

export function ConversationPanel({ client, screen, version, storageKey, T, onTicketSubmit, humanOnly = false }) {
  const [items, setItems] = useState([]), [id, setId] = useState(null), [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [status, setStatus] = useState('open'), [before, setBefore] = useState(null);
  const [article, setArticle] = useState(null);
  const [nextPage, setNextPage] = useState(null), [listBusy, setListBusy] = useState(false), [historyBusy, setHistoryBusy] = useState(false);
  const listPending = useRef(false), historyPending = useRef(false), listGeneration = useRef(0);
  const retry = useRef(null), generation = useRef(0), input = useRef(null);
  const button = { padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.textPrimary, cursor: 'pointer' };
  async function select(nextId) {
    const epoch = ++generation.current;
    setError(''); setId(nextId); setMessages([]); setArticle(null); setBefore(null); retry.current = null;
    historyPending.current = false; setHistoryBusy(false);
    try {
      const data = await client.conversation(nextId);
      if (epoch !== generation.current) return;
      setMessages(data.messages); setStatus(data.status); setBefore(data.next_cursor);
      setItems(previous => previous.some(c => c.conversation_id === nextId) ? previous : [...previous,
        { conversation_id: nextId, title: data.title || 'Saved conversation', status: data.status }]);
      try { localStorage.setItem(`${storageKey}_conversation`, nextId); } catch {}
    } catch (e) { if (epoch === generation.current) setError(e.message); }
  }
  useEffect(() => {
    if (!client) return;
    let active = true;
    client.listConversations().then(data => {
      if (!active) return;
      setItems(data.items);
      setNextPage(data.next_cursor || null);
      let previous; try { previous = localStorage.getItem(`${storageKey}_conversation`); } catch {}
      // Ownership is checked by the detail API even if this conversation is beyond page one.
      if (previous) select(previous);
    }).catch(e => active && setError(e.message));
    return () => { active = false; generation.current++; listGeneration.current++; };
  }, [client, storageKey]);
  useEffect(() => {
    if (!id || !client || busy) return;
    let active = true;
    const timer = setInterval(() => client.conversation(id).then(data => {
      if (active) {
        setMessages(previous => {
          const rows = new Map(previous.map(m => [m.message_id, m]));
          for (const message of data.messages) rows.set(message.message_id, { ...rows.get(message.message_id), ...message });
          return [...rows.values()];
        });
        setStatus(data.status);
      }
    }).catch(() => {}), 10000);
    return () => { active = false; clearInterval(timer); };
  }, [id, client, busy]);
  async function send(human = false) {
    const text = draft.trim() || (human ? 'I would like help from the support team.' : '');
    if (!text || busy || !client) return;
    setBusy(true); setError('');
    try {
      let currentId = id;
      if (!currentId) {
        const created = await client.createConversation({ title: text.slice(0, 100), screen, version });
        currentId = created.conversation_id; setId(currentId);
        try { localStorage.setItem(`${storageKey}_conversation`, currentId); } catch {}
      }
      if (!retry.current || retry.current.text !== text) retry.current = { text, id: crypto.randomUUID(), human };
      const result = await client.sendMessage(currentId, text, retry.current.id, { screen, version, human: retry.current.human });
      if (result.message.ticket_id) onTicketSubmit?.(result.message.ticket_id);
      retry.current = null; setDraft('');
      const data = await client.conversation(currentId);
      setMessages(data.messages); setStatus(data.status); setBefore(data.next_cursor);
      listGeneration.current++;
      const list = await client.listConversations(); setItems(list.items); setNextPage(list.next_cursor || null);
    } catch (e) { setError(`${e.message}. The result could not be confirmed. Retry checks the same message request ID.`); }
    finally { setBusy(false); input.current?.focus(); }
  }
  async function older() {
    if (!before || historyPending.current) return;
    const epoch = generation.current;
    historyPending.current = true; setHistoryBusy(true);
    try { const data = await client.conversation(id, before); if (epoch !== generation.current) return;
      setMessages(previous => { const ids = new Set(previous.map(m => m.message_id)); return [...data.messages.filter(m => !ids.has(m.message_id)), ...previous]; }); setBefore(data.next_cursor); }
    catch (e) { if (epoch === generation.current) setError(e.message); }
    finally { if (epoch === generation.current) { historyPending.current = false; setHistoryBusy(false); } }
  }
  async function moreConversations() {
    if (!nextPage || listPending.current || busy) return;
    const epoch = listGeneration.current;
    listPending.current = true; setListBusy(true);
    try {
      const data = await client.listConversations(nextPage);
      if (epoch !== listGeneration.current) return;
      setItems(previous => { const ids = new Set(previous.map(c => c.conversation_id)); return [...previous, ...data.items.filter(c => !ids.has(c.conversation_id))]; });
      setNextPage(data.next_cursor);
    } catch (e) { if (epoch === listGeneration.current) setError(e.message); }
    finally { listPending.current = false; setListBusy(false); }
  }
  async function showSource(citation) {
    try { setArticle((await client.article(citation.id, citation.revision, version)).article); }
    catch (e) { setError(e.message); }
  }
  async function feedback(message, satisfaction) {
    try { await client.feedback(null, satisfaction, null, message.run_id); setMessages(rows => rows.map(m => m.message_id === message.message_id ? { ...m, feedback: satisfaction } : m)); }
    catch (e) { setError(e.message); }
  }
  return <section style={{ padding: 14, color: T.textPrimary }} aria-label="Support conversation">
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <select aria-label="Choose a conversation" value={id || ''} disabled={busy} onChange={e => e.target.value ? select(e.target.value) : null} style={{ ...button, minWidth: 0, flex: 1 }}>
        <option value="">New conversation</option>{items.map(c => <option key={c.conversation_id} value={c.conversation_id}>{c.title} — {c.status.replaceAll('_', ' ')}</option>)}
      </select>
      <button style={button} disabled={busy} onClick={() => { generation.current++; historyPending.current = false; setHistoryBusy(false); setId(null); setMessages([]); setBefore(null); setArticle(null); setDraft(''); setError(''); retry.current = null; input.current?.focus(); }}>New</button>
    </div>
    {nextPage && <button style={button} disabled={busy || listBusy} onClick={moreConversations}>{listBusy ? 'Loading…' : 'Load more conversations'}</button>}
    {id && <p style={{ fontSize: 12 }}>Status: {status.replaceAll('_', ' ')}</p>}
    {before && <button style={button} disabled={historyBusy || busy} onClick={older}>{historyBusy ? 'Loading…' : 'Load earlier messages'}</button>}
    <div role="log" aria-live="polite" aria-relevant="additions" style={{ maxHeight: '36vh', overflowY: 'auto' }}>
      {!messages.length && <p>Ask a question or contact our support team. Your conversation will be saved.</p>}
      {messages.map(m => <article key={m.message_id} style={{ padding: 10, marginBottom: 10, background: m.role === 'user' ? T.accentLight : T.surface, borderRadius: 8 }}>
        <strong style={{ fontSize: 12 }}>{m.role === 'user' ? 'You' : m.role === 'support' ? 'Support team' : 'Assistant'}</strong>
        <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{m.text}</p>
        {!!m.citations?.length && <details><summary>Sources</summary><ul>{m.citations.map(c => <li key={c.id}><button style={button} onClick={() => showSource(c)}>{c.title}</button></li>)}</ul></details>}
        {m.run_id && m.outcome === 'answered' && !m.feedback && <div><button style={button} onClick={() => feedback(m, 'positive')}>Helpful</button> <button style={button} onClick={() => feedback(m, 'negative')}>Not helpful</button></div>}
        {m.feedback && <small>Feedback saved</small>}
      </article>)}
    </div>
    {article && <section aria-label="Approved source" style={{ border: `1px solid ${T.border}`, padding: 10, marginBottom: 10 }}>
      <strong>{article.title}</strong><p style={{ whiteSpace: 'pre-wrap', maxHeight: '25vh', overflowY: 'auto' }}>{article.content}</p><button style={button} onClick={() => setArticle(null)}>Close source</button>
    </section>}
    {error && <p role="alert" style={{ color: T.negative }}>{error}</p>}
    <label style={{ display: 'block' }}>Your message<textarea ref={input} value={draft} maxLength={8000} onChange={e => setDraft(e.target.value)} disabled={busy}
      style={{ width: '100%', boxSizing: 'border-box', minHeight: 70, margin: '6px 0', padding: 8, borderRadius: 8, border: `1px solid ${T.border}`, font: 'inherit' }} /></label>
    <div style={{ display: 'flex', gap: 8 }}><button style={{ ...button, background: T.accent, color: '#fff' }} disabled={busy || !draft.trim()} onClick={() => send(humanOnly)}>{busy ? 'Saving…' : retry.current ? 'Retry message' : 'Send'}</button>
      <button style={button} disabled={busy} onClick={() => send(true)}>Talk to support</button></div>
  </section>;
}
